import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import flashMessage from '../components/HelperFunctions';
import { Alert, Button, PageHeader, SectionHeading, Skeleton, Surface } from '../components/ui/Primitives';
import {
  getOrganization,
  getOrganizationMembers,
  updateOrganization,
  updateReportPreferences,
} from '../services/organizationApi';
import { logEvent } from '../utils/logger';
import { SUPPORTED_TIMEZONES, supportedTimezoneLabel } from '../utils/timezones';
import './Notifications.css';


const NOTIFY_OPTIONS = [
  { value: 'never', label: 'Never', description: 'Review calls in Sayable whenever you need them.' },
  { value: 'once-end-of-day', label: 'Daily digest', description: 'Receive one summary at your scheduled local time.' },
  { value: 'as-completed', label: 'After each call', description: 'Receive a summary as soon as each call is completed.' },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_unused, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 ? 30 : 0;
  const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const displayHour = hour % 12 || 12;
  return { value, label: `${displayHour}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}` };
});


function errorMessage(error, fallback) {
  return error?.response?.data?.error?.message
    || error?.response?.data?.message
    || error?.response?.data?.detail
    || fallback;
}


function scheduleLabel(timeValue, timezoneValue) {
  const timeLabel = TIME_OPTIONS.find(option => option.value === timeValue)?.label || timeValue;
  const zoneLabel = supportedTimezoneLabel(timezoneValue).replace(/^[A-Za-z]+ - /, '').replace('Standard Time', 'Time');
  return `${timeLabel} · ${zoneLabel}`;
}


function Notifications({ orgMember, embedded = false, refreshVersion = 0, onSaved }) {
  const navigate = useNavigate();
  const organizationId = orgMember?.organization_id;
  const currentMemberId = String(orgMember?.id || orgMember?.user_id || '');
  const canManage = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);
  const [organization, setOrganization] = useState(null);
  const [member, setMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingDefault, setEditingDefault] = useState(false);
  const [frequency, setFrequency] = useState('never');
  const [useCustomSchedule, setUseCustomSchedule] = useState(false);
  const [memberTime, setMemberTime] = useState('17:00');
  const [memberTimezone, setMemberTimezone] = useState('America/Los_Angeles');
  const [defaultTime, setDefaultTime] = useState('17:00');
  const [defaultTimezone, setDefaultTimezone] = useState('America/Los_Angeles');
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingMember, setIsSavingMember] = useState(false);
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!organizationId) {
        setLoadError('Your organization profile is unavailable. Please sign in again.');
        setIsLoading(false);
        return;
      }
      setOrganization(null);
      setMember(null);
      setIsLoading(true);
      setLoadError('');
      try {
        const [nextOrganization, members] = await Promise.all([
          getOrganization(organizationId),
          getOrganizationMembers(organizationId),
        ]);
        if (!active) return;
        const currentMember = members.find(item => String(item.id) === currentMemberId);
        if (!currentMember) throw new Error('Your organization profile could not be found.');
        const organizationTimezone = nextOrganization.daily_report_timezone
          || nextOrganization.timezone
          || 'America/Los_Angeles';
        const organizationTime = nextOrganization.daily_report_time || '17:00';
        setMembers(members);
        setEditingPersonal(false);
        setEditingDefault(false);
        setOrganization(nextOrganization);
        setMember(currentMember);
        setFrequency(currentMember.notifications || 'never');
        setDefaultTime(organizationTime);
        setDefaultTimezone(organizationTimezone);
        setUseCustomSchedule(Boolean(currentMember.daily_report_time && currentMember.daily_report_timezone));
        setMemberTime(currentMember.daily_report_time || organizationTime);
        setMemberTimezone(currentMember.daily_report_timezone || organizationTimezone);
      } catch (error) {
        if (error?.response?.status === 401) {
          localStorage.removeItem('jwt_authorization');
          navigate('/');
          return;
        }
        if (active) setLoadError(errorMessage(error, 'Your notification settings could not be loaded.'));
      } finally {
        if (active) setIsLoading(false);
      }
    }
    load();
    logEvent({ event: { label: 'PageView', data: { page: 'Notifications' } } });
    return () => { active = false; };
  }, [currentMemberId, navigate, organizationId, refreshVersion]);

  const savedDefaultTime = organization?.daily_report_time || '17:00';
  const savedDefaultTimezone = organization?.daily_report_timezone || organization?.timezone || 'America/Los_Angeles';
  const timezoneOptions = value => SUPPORTED_TIMEZONES.some(option => option.value === value)
    ? SUPPORTED_TIMEZONES : [...SUPPORTED_TIMEZONES, { value, label: value }];

  const effectiveSchedule = useMemo(() => (
    useCustomSchedule
      ? scheduleLabel(memberTime, memberTimezone)
      : scheduleLabel(savedDefaultTime, savedDefaultTimezone)
  ), [savedDefaultTime, savedDefaultTimezone, memberTime, memberTimezone, useCustomSchedule]);

  const savePersonalSettings = async event => {
    event.preventDefault();
    if (!member) return;
    setIsSavingMember(true);
    try {
      const updated = await updateReportPreferences(organizationId, member.id, {
        notifications: frequency,
        daily_report_time: useCustomSchedule ? memberTime : null,
        daily_report_timezone: useCustomSchedule ? memberTimezone : null,
      });
      setMember(updated);
      setEditingPersonal(false);
      flashMessage('success', 'Notification settings saved.');
      onSaved?.();
    } catch (error) {
      flashMessage('error', errorMessage(error, 'Notification settings could not be saved.'));
    } finally {
      setIsSavingMember(false);
    }
  };

  const saveOrganizationDefault = async event => {
    event.preventDefault();
    setIsSavingDefault(true);
    try {
      const updated = await updateOrganization(organizationId, {
        daily_report_time: defaultTime,
        daily_report_timezone: defaultTimezone,
      });
      setOrganization(updated);
      setEditingDefault(false);
      setDefaultTime(updated.daily_report_time);
      setDefaultTimezone(updated.daily_report_timezone || updated.timezone || 'America/Los_Angeles');
      flashMessage('success', 'Organization report schedule saved.');
      onSaved?.();
    } catch (error) {
      flashMessage('error', errorMessage(error, 'The organization schedule could not be saved.'));
    } finally {
      setIsSavingDefault(false);
    }
  };

  const editPersonal = () => {
    setFrequency(member.notifications || 'never');
    setUseCustomSchedule(Boolean(member.daily_report_time && member.daily_report_timezone));
    setMemberTime(member.daily_report_time || savedDefaultTime);
    setMemberTimezone(member.daily_report_timezone || savedDefaultTimezone);
    setEditingPersonal(true);
  };
  const editDefault = () => {
    setDefaultTime(savedDefaultTime);
    setDefaultTimezone(savedDefaultTimezone);
    setEditingDefault(true);
  };

  if (isLoading) {
    return (
      <div className={embedded ? 'notifications-page' : 'page-content notifications-page'}>
        <Skeleton width="38%" height={38} />
        <Skeleton height={220} />
        <Skeleton height={180} />
      </div>
    );
  }

  return (
    <div className={embedded ? 'notifications-page' : 'page-content notifications-page'}>
      {!embedded ? <PageHeader
        eyebrow="Email preferences"
        title="Call reports"
        description="Choose when Sayable emails you about completed calls. Daily reports follow your local schedule and current call-access permissions."
      /> : null}

      {loadError ? <Alert tone="danger" title="Settings unavailable">{loadError}</Alert> : null}

      {member ? (
        <Surface className="notification-card">
          <SectionHeading title="Your preferences" description="These settings apply only to your email address." />
          {!editingPersonal ? <div className="report-saved-summary">
            <div><strong>{reportSummary(member, organization)}</strong>
              {member.notifications === 'once-end-of-day' ? <small>{member.daily_report_time ? 'Personal schedule' : 'Following the organization default'}</small> : null}
            </div>
            <Button variant="secondary" aria-label="Edit your report preferences" onClick={editPersonal}>Edit</Button>
          </div> : <form onSubmit={savePersonalSettings}>
            <fieldset className="notification-frequency-grid">
              <legend className="sr-only">Call report frequency</legend>
              {NOTIFY_OPTIONS.map(option => (
                <label key={option.value} className={`notification-frequency-option ${frequency === option.value ? 'selected' : ''}`}>
                  <input type="radio" name="report-frequency" value={option.value} checked={frequency === option.value} onChange={event => setFrequency(event.target.value)} />
                  <span><strong>{option.label}</strong><small>{option.description}</small></span>
                </label>
              ))}
            </fieldset>

            {frequency === 'once-end-of-day' ? (
              <div className="notification-schedule-panel">
                <label className="notification-inherit-toggle" htmlFor="personal-report-schedule">
                  <input
                    id="personal-report-schedule"
                    type="checkbox"
                    checked={useCustomSchedule}
                    onChange={event => {
                      setUseCustomSchedule(event.target.checked);
                      if (event.target.checked) {
                        setMemberTime(member.daily_report_time || savedDefaultTime);
                        setMemberTimezone(member.daily_report_timezone || savedDefaultTimezone);
                      }
                    }}
                  />
                  <span><strong>Use a personal schedule</strong><small>Otherwise, your report follows the organization default.</small></span>
                </label>
                {useCustomSchedule ? (
                  <div className="notification-schedule-fields">
                    <label htmlFor="personal-report-time">Delivery time
                      <select id="personal-report-time" className="say-select" value={memberTime} onChange={event => setMemberTime(event.target.value)}>
                        {TIME_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </label>
                    <label htmlFor="personal-report-timezone">Timezone
                      <select id="personal-report-timezone" className="say-select" value={memberTimezone} onChange={event => setMemberTimezone(event.target.value)}>
                        {timezoneOptions(memberTimezone).map(timezone => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}
                      </select>
                    </label>
                  </div>
                ) : null}
                <p className="notification-effective-schedule"><strong>Your daily digest:</strong> {effectiveSchedule}</p>
              </div>
            ) : null}

            <div className="notification-card-actions">
              <Button type="button" variant="secondary" disabled={isSavingMember} onClick={() => setEditingPersonal(false)}>Cancel</Button>
              <Button type="submit" loading={isSavingMember} loadingLabel="Saving…">Save my preferences</Button>
            </div>
          </form>}
        </Surface>
      ) : null}

      {canManage && organization ? (
        <Surface className="notification-card">
          <SectionHeading
            eyebrow="Daily digest schedule"
            title="Organization defaults"
            description="Members who do not choose a personal schedule receive their daily digest at this time."
          />
          {!editingDefault ? <div className="report-saved-summary">
            <div><strong>Daily at {scheduleLabel(savedDefaultTime, savedDefaultTimezone)}</strong><small>Used by members without a personal schedule.</small></div>
            <Button variant="secondary" aria-label="Edit organization report schedule" onClick={editDefault}>Edit</Button>
          </div> : <form onSubmit={saveOrganizationDefault}>
            <div className="notification-schedule-fields">
              <label htmlFor="default-report-time">Delivery time
                <select id="default-report-time" className="say-select" value={defaultTime} onChange={event => setDefaultTime(event.target.value)}>
                  {TIME_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label htmlFor="default-report-timezone">Timezone
                <select id="default-report-timezone" className="say-select" value={defaultTimezone} onChange={event => setDefaultTimezone(event.target.value)}>
                  {timezoneOptions(defaultTimezone).map(timezone => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}
                </select>
              </label>
            </div>
            <p className="notification-admin-help">Set individual schedules in Member schedules below.</p>
            <div className="notification-card-actions">
              <Button type="button" variant="secondary" disabled={isSavingDefault} onClick={() => setEditingDefault(false)}>Cancel</Button>
              <Button type="submit" loading={isSavingDefault} loadingLabel="Saving…">Save organization schedule</Button>
            </div>
          </form>}
        </Surface>
      ) : null}
      {canManage && organization ? <Surface className="notification-card">
        <SectionHeading title="Member schedules" description="Manage call report preferences for individual team members." />
        {members.filter(person => String(person.id) !== currentMemberId && person.is_active !== false).map(person => (
          <MemberReportSchedule key={person.id} member={person} organization={organization} onSaved={updated => {
            setMembers(current => current.map(item => item.id === updated.id ? updated : item));
            onSaved?.();
          }} />
        ))}
        {!members.some(person => String(person.id) !== currentMemberId && person.is_active !== false) ? <p className="report-member-empty">Other active team members will appear here. Manage your own schedule in Your preferences.</p> : null}
      </Surface> : null}
    </div>
  );
}


function reportSummary(member, organization) {
  if (member.notifications !== 'once-end-of-day') return NOTIFY_OPTIONS.find(option => option.value === member.notifications)?.label || 'Never';
  return `Daily at ${scheduleLabel(member.daily_report_time || organization?.daily_report_time || '17:00', member.daily_report_timezone || organization?.daily_report_timezone || organization?.timezone || 'America/Los_Angeles')}`;
}

function MemberReportSchedule({ member, organization, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [frequency, setFrequency] = useState('never');
  const [custom, setCustom] = useState(false);
  const [time, setTime] = useState('17:00');
  const [zone, setZone] = useState('America/Los_Angeles');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const name = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
  const startEditing = () => {
    setFrequency(member.notifications || 'never');
    setCustom(Boolean(member.daily_report_time && member.daily_report_timezone));
    setTime(member.daily_report_time || organization.daily_report_time || '17:00');
    setZone(member.daily_report_timezone || organization.daily_report_timezone || organization.timezone || 'America/Los_Angeles');
    setError('');
    setEditing(true);
  };
  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updated = await updateReportPreferences(organization.id, member.id, {
        notifications: frequency, daily_report_time: custom ? time : null, daily_report_timezone: custom ? zone : null,
      });
      onSaved(updated);
      setEditing(false);
      flashMessage('success', 'Member report preferences saved.');
    } catch (error) {
      setError(errorMessage(error, 'Member report preferences could not be saved.'));
    } finally { setSaving(false); }
  };
  const zones = SUPPORTED_TIMEZONES.some(option => option.value === zone) ? SUPPORTED_TIMEZONES : [...SUPPORTED_TIMEZONES, { value: zone, label: zone }];
  return <div className="report-member-row">
    <div className="report-saved-summary"><div><strong>{name}</strong><small>{reportSummary(member, organization)}{member.notifications === 'once-end-of-day' && !member.daily_report_time ? ' · Organization default' : ''}</small></div>
      {!editing ? <Button variant="secondary" aria-label={`Edit report preferences for ${name}`} onClick={startEditing}>Edit</Button> : null}
    </div>
    {editing ? <form onSubmit={save} aria-label={`Report preferences for ${name}`}>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <fieldset className="report-member-fields" disabled={saving}>
        <legend className="sr-only">{name}'s report preferences</legend>
        <label>Report frequency<select className="say-select" value={frequency} onChange={event => setFrequency(event.target.value)}>
          {NOTIFY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select></label>
        {frequency === 'once-end-of-day' ? <>
          <label className="notification-inherit-toggle"><input type="checkbox" checked={custom} onChange={event => setCustom(event.target.checked)} />Use a personal schedule</label>
          {custom ? <div className="notification-schedule-fields">
            <label>Delivery time<select className="say-select" value={time} onChange={event => setTime(event.target.value)}>{TIME_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <label>Timezone<select className="say-select" value={zone} onChange={event => setZone(event.target.value)}>{zones.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          </div> : null}
        </> : null}
        <div className="notification-card-actions"><Button type="button" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button><Button type="submit" loading={saving}>Save member preferences</Button></div>
      </fieldset>
    </form> : null}
  </div>;
}

export default Notifications;
